import React from "react";
import { connect, useDispatch } from "react-redux";
import { getWidgetSpecificIsVisible, executeActions } from "@utils/index";
import { useState, useEffect } from "react";
import { updateLoader, fetchConfigFailure } from "@store/slices/config";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";
import { Input } from "@acko-tech/building-blocks.ui.input";
import LocationPicker from "@widgets/LocationPicker";
import Icon from "@widgets/Icon";
import { useLocationPicker } from "@utils/hooks/useLocationPicker";
import { BasicLoader } from "@acko-tech/building-blocks.ui.loaders";
import AddressPrecisionWarning from "@components/addressPrecisionWarning";
import { isWideAreaPlaceSelection } from "@utils/addressPrecision";
import {
  Container,
  RowWrapper,
  Row,
  Title,
  Description,
  SearchTitle,
} from "./styles";

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    ),
  };
};

const AddressList = ({ is_visible, actions, searchText, enableAddressPrecisionWarning = true }: any) => {
  const dispatch = useDispatch();
  const [address, setAddress] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [showAddressPrecisionWarning, setShowAddressPrecisionWarning] = useState(false);
  const {fetchingLocation} = useLocationPicker();

  useEffect(() => {
    actions?.on_load?.forEach((func: any) => {
      func.funCall?.(func.args);
    });
  }, []);

  if (!is_visible) return <></>;

  const handleSelect = async (suggestion: any) => {
    if (!suggestion) return;
    if (enableAddressPrecisionWarning && isWideAreaPlaceSelection(suggestion.types)) {
      setShowAddressPrecisionWarning(true);
      return;
    }
    setShowAddressPrecisionWarning(false);
    dispatch(updateLoader(true));
    geocodeByAddress(suggestion.description)
      .then((results: any) => {
        let postalCode = null;
        const selectedAddress = results?.find((result: any) => {
          return result.address_components.some((item: any) => {
            if (item.types.includes("postal_code")) {
              postalCode = item.long_name;
              return true;
            }

            return false;
          });
        });
        const place = selectedAddress || results[0];
        const cityDetails = place?.address_components?.find((item: any) => {
          if (
            item.types.includes("locality") &&
            item.types.includes("political")
          )
            return true;
          return false;
        });
        const stateDetails = place?.address_components?.find((item: any) => {
          if (item.types.includes("administrative_area_level_1")) return true;
          return false;
        });
        const {
          geometry: {
            location: { lat, lng },
          },
        } = place;
        const data = {
          lat: lat(),
          lng: lng(),
          postalCode,
          city: cityDetails ? cityDetails.long_name : "",
          fullAddress: suggestion.description,
          state: stateDetails ? stateDetails.long_name : "",
          ...suggestion.formattedSuggestion,
        };
        executeActions(actions?.on_click || [], {
          payloadData: {
            address: data,
            pincode: postalCode
          }
        });
        dispatch(updateLoader(false));
      })
      .catch((error: any) => {
        dispatch(fetchConfigFailure(error?.message));
      });
  };
  return (
    <Container>
      <PlacesAutocomplete
        value={address}
        onChange={(value) => {
          setAddress(value);
          setShowAddressPrecisionWarning(false);
        }}
        searchOptions={{
          componentRestrictions: { country: ["in"] },
        }}
        debounce={300}
        onSelect={(_address, _placeId, suggestion) => handleSelect(suggestion)}
        googleCallbackName="googlePlaceAutocomplete"
        onError={(status,) => {
          console.error("PlacesAutocomplete error:", status);
          console.error("Error details:", {
            status,
            timestamp: new Date().toISOString(),
            address: address
          });
        }}
      >
        {({
          getInputProps,
          suggestions,
          getSuggestionItemProps,
          loading,
        }: any) => {
          const inputProps = getInputProps({
            className: "location-search-input",
          });
          return (
            <div onClick={(e) => e.stopPropagation()}>
              {/* The building-block Input does not forward onKeyDown. */}
              <div onKeyDown={inputProps.onKeyDown}>
                <Input
                  type={"text"}
                  disabled={fetchingLocation}
                  labelText={searchText}
                  leftElement={
                    !isFocused ? (
                      <Icon
                        src={"/images/icons/search-icon.svg"}
                        overrideIsVisible={true}
                        styles={{
                          height: "20px",
                          width: "20px",
                          marginTop: "5px"
                        }}
                      />
                    ) : null
                  }
                  {...inputProps}
                  onBlur={(event) => {
                    inputProps.onBlur(event);
                    setIsFocused(false);
                  }}
                  onfocus={() => setIsFocused(true)}
                />
              </div>
              <div onClick={() => setShowAddressPrecisionWarning(false)}>
                <LocationPicker is_visible={true} actions={actions} />
              </div>
              {showAddressPrecisionWarning ? (
                <div style={{ marginTop: "20px" }}>
                  <AddressPrecisionWarning />
                </div>
              ) : <div style={{ marginTop: "20px" }}>
                {suggestions.length == 0 && loading && <BasicLoader/>}
                {suggestions.length > 0 && 
                <SearchTitle> Search Results</SearchTitle>}
                {suggestions.map((suggestion: any) => {
                  return (
                    <RowWrapper
                      key={suggestion.placeId}
                    >
                      <Row {...getSuggestionItemProps(suggestion)}>
                        <div style={{ display: "flex", width: "100%" }}>
                          <Icon
                            src={"/images/location-picker.svg"}
                            overrideIsVisible={true}
                            styles={{
                              height: "24px",
                              width: "24px",
                              marginRight: "12px",
                              marginTop: "4px",
                            }}
                          />
                          <div style={{ width: "100%" }}>
                            <Title>
                              {suggestion?.formattedSuggestion?.mainText}
                            </Title>
                            <Description>
                              {suggestion?.formattedSuggestion?.secondaryText}
                            </Description>
                          </div>
                        </div>
                      </Row>
                    </RowWrapper>
                  );
                })}
              </div>}
            </div>
          );
        }}
      </PlacesAutocomplete>
    </Container>
  );
};

export default connect(mapStateToProps)(AddressList);
